import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";
import {
	ArrowLeftOutlined,
	DollarOutlined,
	SettingOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Progress,
	Row,
	Space,
	Statistic,
	Table,
	Tag,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { AssetDetail } from "./AssetDetail";

// ===================================
// INTERFACES
// ===================================

interface AssetBreakdownItem {
	id: string;
	name: string;
	assetType: string;
	currentValue: number;
	replacementCost: number;
	healthScore: number;
	usefulLife: number;
	ageInYears: number;
	location: string;
	priorityScore: "High" | "Medium" | "Low";
	lastMaintenance?: string;
	condition: "Excellent" | "Good" | "Fair" | "Poor" | "Critical";
}

interface AssetBreakdownProps {
	year: string;
	assetType: string;
	totalCost: number;
	totalCount: number;
	onBack: () => void;
	organizationId: string;
	isYAxisReplCost: boolean;
}

// ===================================
// COMPONENT
// ===================================

const AssetBreakdown = ({
	year,
	assetType,
	totalCost,
	totalCount,
	onBack,
	organizationId,
	isYAxisReplCost,
}: AssetBreakdownProps) => {
	// ===================================
	// STATE
	// ===================================

	const [loading, setLoading] = useState(true);
	const [assets, setAssets] = useState<AssetBreakdownItem[]>([]);
	const [pageSize, setPageSize] = useState(20);
	const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

	// ===================================
	// EFFECTS
	// ===================================

	useEffect(() => {
		fetchAssetBreakdown();
	}, [year, assetType, organizationId]);

	// ===================================
	// API FUNCTIONS
	// ===================================

	const fetchAssetBreakdown = async () => {
		try {
			setLoading(true);

			// Make API call to get actual asset breakdown data
			const url = `${VITE_CORE_API_URL}${CORE_URL}/organization/predspend/${organizationId}/breakdown?year=${year}&assetType=${encodeURIComponent(assetType)}`;

			const response = await fetch(url, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to fetch asset breakdown (${response.status})`);
			}

			const result = await response.json();

			// Handle the response - it could be wrapped in a success object or be direct array
			const assetData = Array.isArray(result) ? result : result.data || [];

			setAssets(assetData);
		} catch (error) {
			console.error("Failed to fetch asset breakdown:", error);
			message.error("Failed to load asset breakdown data. Please try again.");
			setAssets([]);
		} finally {
			setLoading(false);
		}
	};

	// ===================================
	// TABLE CONFIGURATION
	// ===================================

	const columns: ColumnsType<AssetBreakdownItem> = [
		{
			title: "Asset Tag",
			dataIndex: "assetTag",
			key: "assetTag",
			fixed: "left",
			width: 140,
			render: (assetTag: string) => assetTag || "-",
		},
		{
			title: "Location",
			dataIndex: "location",
			key: "location",
			width: 180,
		},
		{
			title: "Health Score",
			dataIndex: "healthScore",
			key: "healthScore",
			width: 120,
			render: (score: number) => (
				<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<Progress
						percent={score}
						size="small"
						strokeColor={
							score > 60 ? "#52c41a" : score > 40 ? "#faad14" : "#ff4d4f"
						}
						style={{ width: "60px" }}
						showInfo={false}
					/>
					<span>{score}%</span>
				</div>
			),
		},
		{
			title: "Condition",
			dataIndex: "condition",
			key: "condition",
			width: 100,
			render: (condition: string) => {
				const color =
					condition === "Excellent"
						? "green"
						: condition === "Good"
							? "blue"
							: condition === "Fair"
								? "orange"
								: condition === "Poor"
									? "red"
									: "volcano";
				return <Tag color={color}>{condition}</Tag>;
			},
		},
		{
			title: "Priority",
			dataIndex: "priorityScore",
			key: "priorityScore",
			width: 100,
			render: (priority: string) => {
				const color =
					priority === "High"
						? "red"
						: priority === "Medium"
							? "orange"
							: "green";
				return <Tag color={color}>{priority}</Tag>;
			},
		},
		{
			title: "Current Value",
			dataIndex: "currentValue",
			key: "currentValue",
			width: 130,
			render: (value: number) =>
				new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				}).format(value),
		},
		{
			title: "Replacement Cost",
			dataIndex: "replacementCost",
			key: "replacementCost",
			width: 150,
			render: (cost: number) =>
				new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				}).format(cost),
		},
		{
			title: "Model",
			dataIndex: "modelNumber",
			key: "modelNumber",
			width: 160,
			render: (modelNumber: string) => modelNumber || "-",
		},
		{
			title: "Serial Number",
			dataIndex: "serialNumber",
			key: "serialNumber",
			width: 170,
			render: (serialNumber: string) => serialNumber || "-",
		},
	];

	// ===================================
	// UTILITY FUNCTIONS
	// ===================================

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	const averageHealthScore =
		assets.length > 0
			? Math.round(
					assets.reduce((sum, asset) => sum + asset.healthScore, 0) /
						assets.length,
				)
			: 0;

	const highPriorityCount = assets.filter(
		(asset) => asset.priorityScore === "High",
	).length;

	// ===================================
	// RENDER
	// ===================================

	// If an asset is selected, show the asset detail view
	if (selectedAssetId) {
		return (
			<AssetDetail
				assetId={selectedAssetId}
				onBack={() => setSelectedAssetId(null)}
				onNavigateToAsset={(assetId: string) => setSelectedAssetId(assetId)}
			/>
		);
	}

	return (
		<div className="asset-breakdown">
			{/* =============== HEADER SECTION =============== */}
			<div className="pred-spend-header">
				<div className="header-content">
					<div className="header-title">
						<h2>
							{assetType} Assets - {year}
						</h2>
						<p>
							Detailed breakdown of assets scheduled for replacement in {year}
						</p>
					</div>
					<div className="header-actions">
						<Space>
							<Button icon={<ArrowLeftOutlined />} onClick={onBack}>
								Back to Overview
							</Button>
						</Space>
					</div>
				</div>
			</div>

			{/* Summary Cards */}
			<div style={{ marginBottom: "24px" }}>
				<Row gutter={[16, 16]}>
					<Col xs={24} sm={6}>
						<Card>
							<Statistic
								title="Total Assets"
								value={totalCount}
								prefix={<SettingOutlined />}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={6}>
						<Card>
							<Statistic
								title="Total Cost"
								value={totalCost}
								formatter={(value) => formatCurrency(Number(value))}
								prefix={<DollarOutlined />}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={6}>
						<Card>
							<Statistic
								title="Avg Health Score"
								value={averageHealthScore}
								formatter={(value) => `${value}%`}
								valueStyle={{
									color:
										averageHealthScore > 60
											? "#52c41a"
											: averageHealthScore > 40
												? "#faad14"
												: "#ff4d4f",
								}}
							/>
						</Card>
					</Col>
					<Col xs={24} sm={6}>
						<Card>
							<Statistic
								title="High Priority"
								value={highPriorityCount}
								suffix={`/ ${totalCount}`}
								valueStyle={{
									color: highPriorityCount > 0 ? "#ff4d4f" : "#52c41a",
								}}
							/>
						</Card>
					</Col>
				</Row>
			</div>

			{/* Assets Table */}
			<Card title="Asset Details" className="breakdown-table">
				<Table
					columns={columns}
					dataSource={loading ? [] : assets}
					rowKey="id"
					loading={loading}
					scroll={{ x: 1290 }}
					onRow={(record) => ({
						onClick: () => {
							setSelectedAssetId(record.id);
						},
						style: { cursor: "pointer" },
						title: "Click to view asset details",
					})}
					pagination={{
						pageSize: pageSize,
						showSizeChanger: true,
						pageSizeOptions: ["10", "20", "50", "100"],
						onShowSizeChange: (current, size) => {
							setPageSize(size);
						},
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} of ${total} assets`,
					}}
				/>
			</Card>
		</div>
	);
};

export { AssetBreakdown };
export type { AssetBreakdownProps, AssetBreakdownItem };
